import React, { useEffect, useState } from 'react';
import {
  Box, Card, CardContent, Chip, CircularProgress, Divider,
  Grid, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Typography,
} from '@mui/material';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, Cell,
} from 'recharts';
import useToken from '../../hooks/useToken';
import { TM_COLOR, NO_TM_COLOR } from './constants';

const STEP_ORDER = [
  'NOT_STARTED', 'CONSENT', 'PRE_EXPERIMENT', 'DOCUMENTATION_INTRO',
  'PLATFORM_TUTORIAL', 'DOCUMENTATION_REVIEW', 'PENDING', 'EXPERIMENT',
  'WEBSITE1', 'WEBSITE2', 'POST_EXPERIMENT', 'END',
];

function StatCard({ label, value, color }) {
  return (
    <Card variant="outlined" sx={{ textAlign: 'center', py: 1 }}>
      <CardContent sx={{ pb: '8px !important' }}>
        <Typography variant="h4" fontWeight="bold" color={color || 'text.primary'}>
          {value ?? '—'}
        </Typography>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
      </CardContent>
    </Card>
  );
}

function SectionHeader({ children }) {
  return (
    <Box sx={{ mt: 4, mb: 1.5 }}>
      <Typography variant="h6" fontWeight="bold">{children}</Typography>
      <Divider />
    </Box>
  );
}

function avg(arr) {
  const valid = arr.filter(v => v !== null && v !== undefined);
  if (!valid.length) return null;
  return valid.reduce((s, v) => s + v, 0) / valid.length;
}

function groupCompareData(participants, field, label) {
  const tm = participants.filter(p => p.tm_group).map(p => p[field]);
  const noTm = participants.filter(p => !p.tm_group).map(p => p[field]);
  return [
    { group: 'TM', [label]: avg(tm) !== null ? +avg(tm).toFixed(2) : null },
    { group: 'No-TM', [label]: avg(noTm) !== null ? +avg(noTm).toFixed(2) : null },
  ];
}

export default function ResearchDashboard() {
  const { getValidToken } = useToken();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function load() {
      try {
        const token = await getValidToken();
        const res = await fetch('/api/admin/research-metrics', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        setData(await res.json());
      } catch (e) {
        setError(e.message);
      }
    }
    load();
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  if (error) {
    return <Box p={3}><Typography color="error">Failed to load metrics: {error}</Typography></Box>;
  }
  if (!data) {
    return <Box p={3} display="flex" alignItems="center" gap={2}><CircularProgress size={20} /><Typography>Loading metrics…</Typography></Box>;
  }

  const { overview, per_participant: participants, vulnerability_distribution: vulnDist } = data;
  const tmParticipants = participants.filter(p => p.tm_group);
  const noTmParticipants = participants.filter(p => !p.tm_group);

  // ── Progress bar data ──────────────────────────────────────────────────────
  const progressData = STEP_ORDER
    .filter(s => (overview.progress_distribution[s] ?? 0) > 0)
    .map(s => ({ step: s.replace(/_/g, ' '), count: overview.progress_distribution[s] }));

  // ── RQ1: avg findings per group ────────────────────────────────────────────
  const findingsCompare = groupCompareData(participants, 'finding_count', 'Avg Findings');

  // ── RQ1: vulnerability type distribution ──────────────────────────────────
  const allVtypes = Array.from(new Set([
    ...Object.keys(vulnDist.TM),
    ...Object.keys(vulnDist['No-TM']),
  ]));
  const vulnChartData = allVtypes.map(vt => ({
    type: vt,
    TM: vulnDist.TM[vt] || 0,
    'No-TM': vulnDist['No-TM'][vt] || 0,
  }));

  // ── RQ2: efficiency comparison ─────────────────────────────────────────────
  const ttffData = groupCompareData(participants, 'time_to_first_finding_minutes', 'Avg Minutes');
  const fpmData = groupCompareData(participants, 'findings_per_minute', 'Avg per Minute');
  const durationData = groupCompareData(participants, 'testing_duration_minutes', 'Avg Minutes');
  const docReviewData = groupCompareData(participants, 'doc_review_duration_minutes', 'Avg Minutes');

  // ── RQ3: DFD interactions (TM group only) ─────────────────────────────────
  const dfdData = tmParticipants.map(p => ({
    name: p.full_name || p.email,
    'DFD Clicks': p.dfd_clicks,
    'Threat Clicks': p.threat_clicks,
  }));

  return (
    <Box p={3}>
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        Research Dashboard
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        Live view of experiment data — auto-derived from DB logs.
      </Typography>

      {/* ── OVERVIEW ── */}
      <SectionHeader>Participant Overview</SectionHeader>
      <Grid container spacing={2}>
        <Grid item xs={6} sm={3}><StatCard label="Total Participants" value={overview.total_participants} /></Grid>
        <Grid item xs={6} sm={3}><StatCard label="TM Group (B)" value={overview.tm_group_count} color={TM_COLOR} /></Grid>
        <Grid item xs={6} sm={3}><StatCard label="No-TM Group (A)" value={overview.no_tm_group_count} color={NO_TM_COLOR} /></Grid>
        <Grid item xs={6} sm={3}><StatCard label="Completed" value={overview.completed_count} color="success.main" /></Grid>
      </Grid>

      {progressData.length > 0 && (
        <Box mt={2}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>Progress step distribution</Typography>
          <ResponsiveContainer width="100%" height={140}>
            <BarChart data={progressData} margin={{ top: 4, right: 16, left: 0, bottom: 40 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="step" tick={{ fontSize: 11 }} angle={-30} textAnchor="end" />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="count" fill="#7986cb" />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      )}

      {/* ── RQ1: EFFECTIVENESS ── */}
      <SectionHeader>RQ1 — Effectiveness</SectionHeader>
      <Grid container spacing={3}>
        <Grid item xs={12} md={5}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Average findings per group
          </Typography>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={findingsCompare} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="group" />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="Avg Findings" radius={[4, 4, 0, 0]}>
                {findingsCompare.map((entry) => (
                  <Cell key={entry.group} fill={entry.group === 'TM' ? TM_COLOR : NO_TM_COLOR} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Grid>

        {vulnChartData.length > 0 && (
          <Grid item xs={12} md={7}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Vulnerability type distribution by group
            </Typography>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={vulnChartData} margin={{ top: 4, right: 16, left: 0, bottom: 50 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="type" tick={{ fontSize: 11 }} angle={-30} textAnchor="end" />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend verticalAlign="top" />
                <Bar dataKey="TM" fill={TM_COLOR} />
                <Bar dataKey="No-TM" fill={NO_TM_COLOR} />
              </BarChart>
            </ResponsiveContainer>
          </Grid>
        )}
      </Grid>

      {/* Per-participant findings table */}
      <Box mt={2}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Per-participant findings
        </Typography>
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 320 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Participant</TableCell>
                <TableCell>Group</TableCell>
                <TableCell>Website</TableCell>
                <TableCell align="right">Findings</TableCell>
                <TableCell align="right">Deleted</TableCell>
                <TableCell align="right">TM Ref.</TableCell>
                <TableCell align="right">Doc Ref.</TableCell>
                <TableCell>Progress</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {participants.map(p => (
                <TableRow key={p.account_id} hover>
                  <TableCell>{p.full_name || p.email}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={p.tm_group ? 'TM' : 'No-TM'}
                      sx={{
                        bgcolor: p.tm_group ? TM_COLOR : NO_TM_COLOR,
                        color: '#fff',
                        fontSize: 11,
                      }}
                    />
                  </TableCell>
                  <TableCell>{p.website || '—'}</TableCell>
                  <TableCell align="right">{p.finding_count}</TableCell>
                  <TableCell align="right">{p.deleted_count}</TableCell>
                  <TableCell align="right">{p.tm_referenced_count}</TableCell>
                  <TableCell align="right">{p.doc_referenced_count}</TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {p.progress_step?.replace(/_/g, ' ')}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* ── RQ2: EFFICIENCY ── */}
      <SectionHeader>RQ2 — Efficiency</SectionHeader>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Time-to-first finding (min)
          </Typography>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={ttffData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="group" />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v) => v !== null ? `${v} min` : 'n/a'} />
              <Bar dataKey="Avg Minutes" radius={[4, 4, 0, 0]}>
                {ttffData.map(e => <Cell key={e.group} fill={e.group === 'TM' ? TM_COLOR : NO_TM_COLOR} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Findings per minute
          </Typography>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={fpmData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="group" />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="Avg per Minute" radius={[4, 4, 0, 0]}>
                {fpmData.map(e => <Cell key={e.group} fill={e.group === 'TM' ? TM_COLOR : NO_TM_COLOR} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Testing duration (min)
          </Typography>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={durationData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="group" />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v) => v !== null ? `${v} min` : 'n/a'} />
              <Bar dataKey="Avg Minutes" radius={[4, 4, 0, 0]}>
                {durationData.map(e => <Cell key={e.group} fill={e.group === 'TM' ? TM_COLOR : NO_TM_COLOR} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Doc review duration (min)
          </Typography>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={docReviewData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="group" />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v) => v !== null ? `${v} min` : 'n/a'} />
              <Bar dataKey="Avg Minutes" radius={[4, 4, 0, 0]}>
                {docReviewData.map(e => <Cell key={e.group} fill={e.group === 'TM' ? TM_COLOR : NO_TM_COLOR} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Grid>
      </Grid>

      {/* Per-participant timing table */}
      <Box mt={2}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Per-participant timing
        </Typography>
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 280 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Participant</TableCell>
                <TableCell>Group</TableCell>
                <TableCell align="right">Duration (min)</TableCell>
                <TableCell align="right">TTFF (min)</TableCell>
                <TableCell align="right">Findings/min</TableCell>
                <TableCell align="right">Doc Review (min)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {participants.map(p => (
                <TableRow key={p.account_id} hover>
                  <TableCell>{p.full_name || p.email}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={p.tm_group ? 'TM' : 'No-TM'}
                      sx={{ bgcolor: p.tm_group ? TM_COLOR : NO_TM_COLOR, color: '#fff', fontSize: 11 }}
                    />
                  </TableCell>
                  <TableCell align="right">{p.testing_duration_minutes ?? '—'}</TableCell>
                  <TableCell align="right">{p.time_to_first_finding_minutes ?? '—'}</TableCell>
                  <TableCell align="right">{p.findings_per_minute ?? '—'}</TableCell>
                  <TableCell align="right">{p.doc_review_duration_minutes ?? '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* ── DOCUMENT REVIEW QUIZ ── */}
      <SectionHeader>Document Review Quiz</SectionHeader>
      {(() => {
        const drScoreData = [
          { group: 'TM', 'Avg Score %': avg(tmParticipants.filter(p => p.dr_score_pct !== null).map(p => p.dr_score_pct))?.toFixed(1) ?? null },
          { group: 'No-TM', 'Avg Score %': avg(noTmParticipants.filter(p => p.dr_score_pct !== null).map(p => p.dr_score_pct))?.toFixed(1) ?? null },
        ];
        const drParticipants = participants.filter(p => p.dr_total > 0);
        return (
          <>
            <Grid container spacing={3} alignItems="flex-start">
              <Grid item xs={12} md={5}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Avg quiz score by group (% of answered questions correct)
                </Typography>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={drScoreData} margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="group" />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
                    <Tooltip formatter={v => v !== null ? `${v}%` : 'n/a'} />
                    <Bar dataKey="Avg Score %" radius={[4, 4, 0, 0]}>
                      {drScoreData.map(e => (
                        <Cell key={e.group} fill={e.group === 'TM' ? TM_COLOR : NO_TM_COLOR} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Grid>

              <Grid item xs={12} md={7}>
                {drParticipants.length === 0 ? (
                  <Typography color="text.secondary">No quiz submissions yet.</Typography>
                ) : (
                  <>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Per-participant quiz results
                    </Typography>
                    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 280 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell>Participant</TableCell>
                            <TableCell>Group</TableCell>
                            <TableCell align="right">Score %</TableCell>
                            <TableCell align="right">Answered / Total</TableCell>
                            <TableCell align="center">Timed Out</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {drParticipants.map(p => (
                            <TableRow key={p.account_id} hover>
                              <TableCell>{p.full_name || p.email}</TableCell>
                              <TableCell>
                                <Chip
                                  size="small"
                                  label={p.tm_group ? 'TM' : 'No-TM'}
                                  sx={{ bgcolor: p.tm_group ? TM_COLOR : NO_TM_COLOR, color: '#fff', fontSize: 11 }}
                                />
                              </TableCell>
                              <TableCell align="right">
                                {p.dr_score_pct !== null ? `${p.dr_score_pct}%` : '—'}
                              </TableCell>
                              <TableCell align="right">
                                {p.dr_num_answered} / {p.dr_total}
                              </TableCell>
                              <TableCell align="center">
                                {p.dr_timed_out === null ? '—' : p.dr_timed_out ? 'Yes' : 'No'}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                )}
              </Grid>
            </Grid>
          </>
        );
      })()}

      {/* ── RQ3: ENGAGEMENT / TM INTERACTIONS ── */}
      <SectionHeader>RQ3 — TM Interactions (Group B only)</SectionHeader>

      {tmParticipants.length === 0 ? (
        <Typography color="text.secondary">No TM-group participants yet.</Typography>
      ) : (
        <>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                DFD interaction counts per participant
              </Typography>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={dfdData} margin={{ top: 4, right: 16, left: 0, bottom: 50 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} angle={-30} textAnchor="end" />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend verticalAlign="top" />
                  <Bar dataKey="DFD Clicks" fill="#388e3c" />
                  <Bar dataKey="Threat Clicks" fill="#f57c00" />
                </BarChart>
              </ResponsiveContainer>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Group avg: DFD interactions vs document views
              </Typography>
              {(() => {
                const rows = [
                  {
                    metric: 'DFD Clicks',
                    TM: avg(tmParticipants.map(p => p.dfd_clicks))?.toFixed(1),
                    'No-TM': avg(noTmParticipants.map(p => p.dfd_clicks))?.toFixed(1),
                  },
                  {
                    metric: 'Threat Clicks',
                    TM: avg(tmParticipants.map(p => p.threat_clicks))?.toFixed(1),
                    'No-TM': avg(noTmParticipants.map(p => p.threat_clicks))?.toFixed(1),
                  },
                  {
                    metric: 'Doc Views',
                    TM: avg(tmParticipants.map(p => p.doc_views))?.toFixed(1),
                    'No-TM': avg(noTmParticipants.map(p => p.doc_views))?.toFixed(1),
                  },
                ];
                return (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Metric</TableCell>
                          <TableCell align="right" sx={{ color: TM_COLOR }}>TM (avg)</TableCell>
                          <TableCell align="right" sx={{ color: NO_TM_COLOR }}>No-TM (avg)</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {rows.map(r => (
                          <TableRow key={r.metric} hover>
                            <TableCell>{r.metric}</TableCell>
                            <TableCell align="right">{r.TM ?? '—'}</TableCell>
                            <TableCell align="right">{r['No-TM'] ?? '—'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                );
              })()}
            </Grid>
          </Grid>

          {/* Detailed TM interaction table */}
          <Box mt={2}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              TM group — per-participant interaction detail
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 280 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Participant</TableCell>
                    <TableCell align="right">DFD Clicks</TableCell>
                    <TableCell align="right">Threat Clicks</TableCell>
                    <TableCell align="right">Doc Views</TableCell>
                    <TableCell align="right">Findings</TableCell>
                    <TableCell align="right">TM Ref. in Findings</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tmParticipants.map(p => (
                    <TableRow key={p.account_id} hover>
                      <TableCell>{p.full_name || p.email}</TableCell>
                      <TableCell align="right">{p.dfd_clicks}</TableCell>
                      <TableCell align="right">{p.threat_clicks}</TableCell>
                      <TableCell align="right">{p.doc_views}</TableCell>
                      <TableCell align="right">{p.finding_count}</TableCell>
                      <TableCell align="right">{p.tm_referenced_count}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </>
      )}
    </Box>
  );
}
